// src/components/BookCover/VersionSelector.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SendIcon from '@mui/icons-material/Send';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useVersionFrontThumbnail from '../../hooks/useVersionFrontThumbnail';

// Renders the cover preview area (image / live thumbnail / placeholder).
// Lives at the top of each card in the version grid.
function VersionCardPreview({ version }) {
  // Skip the expensive Fabric render if we already have a server image.
  const renderedUrl = useVersionFrontThumbnail(
    version.cover_url ? null : version,
    { targetWidth: 280 },
  );

  if (version.cover_url) {
    return (
      <img
        src={version.cover_url}
        alt={version.name}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    );
  }
  if (renderedUrl) {
    return (
      <img
        src={renderedUrl}
        alt={version.name}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    );
  }
  if (version.hasDesign || version.hasWraparoundDesign || version.wraparoundDesign) {
    return (
      <Box sx={{ textAlign: 'center', color: '#666', p: 1 }}>
        <EditIcon sx={{ fontSize: 32, mb: 0.5, opacity: 0.5 }} />
        <Typography variant="caption" display="block">Draft</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ textAlign: 'center', color: '#999', p: 1 }}>
      <AddIcon sx={{ fontSize: 32, mb: 0.5, opacity: 0.3 }} />
      <Typography variant="caption" display="block">Empty</Typography>
    </Box>
  );
}

/**
 * VersionSelector - Thumbnail grid for managing cover versions
 * 
 * Props:
 * - versions: Array of version objects { id, name, cover_url, coverStatus, hasDesign, createdByRole, isOwner }
 * - activeVersionId: Currently active version ID
 * - onSelectVersion: (versionId) => void - Switch to a version
 * - onCreateVersion: () => void - Create new version
 * - onRenameVersion: (versionId, newName) => void - Rename a version
 * - onDeleteVersion: (versionId) => void - Delete a version
 * - onDuplicateVersion: (versionId) => void - Duplicate a version (creates blank version)
 * - onSendToAuthor: (versionId) => void - Designer sends version to author for approval
 * - disabled: Boolean - Disable interactions (e.g., during submission)
 * - canEdit: Boolean - Whether user has edit permission
 * - isAuthor: Boolean - Whether current user is the author
 * - isDesigner: Boolean - Whether current user is a designer
 * - onViewTemplates: () => void - Open the template browser (optional)
 */
export default function VersionSelector({
  versions = [],
  activeVersionId,
  primaryVersionId,
  onSelectVersion,
  onCreateVersion,
  onRenameVersion,
  onDeleteVersion,
  onDuplicateVersion,
  onSendToAuthor,
  onSetPrimary,
  onViewDetails,
  onAddCollaborators,
  onViewTemplates,
  disabled = false,
  canEdit = true,
  loading = false,
  isAuthor = false,
  isDesigner = false,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuVersionId, setMenuVersionId] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteVersionId, setDeleteVersionId] = useState(null);

  // Carousel scroll state
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  useEffect(() => {
    updateScrollState();
  }, [versions]);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  const handleMenuOpen = (event, versionId) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuVersionId(versionId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    // Don't clear menuVersionId here - dialogs need it
  };

  const handleRenameClick = () => {
    const version = versions.find(v => v.id === menuVersionId);
    setRenameValue(version?.name || '');
    setRenameDialogOpen(true);
    handleMenuClose();
  };

  const handleRenameConfirm = () => {
    if (renameValue.trim() && menuVersionId) {
      onRenameVersion?.(menuVersionId, renameValue.trim());
    }
    setRenameDialogOpen(false);
    setRenameValue('');
    setMenuVersionId(null);
  };

  const handleDeleteClick = () => {
    setDeleteVersionId(menuVersionId); // Store the version ID before closing menu
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (deleteVersionId) {
      onDeleteVersion?.(deleteVersionId);
    }
    setDeleteDialogOpen(false);
    setDeleteVersionId(null);
    setMenuVersionId(null);
  };

  const handleDuplicateClick = () => {
    if (menuVersionId) {
      onDuplicateVersion?.(menuVersionId);
    }
    handleMenuClose();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      in_progress: { label: 'In Progress', color: '#D4AF37', bg: '#fef3c7' },
      in_review: { label: 'In Review', color: '#f59e0b', bg: '#fef3c7' },
      approved: { label: 'Approved', color: '#10b981', bg: '#d1fae5' },
      rejected: { label: 'Rejected', color: '#ef4444', bg: '#fee2e2' },
      waiting_for_approval: { label: 'Awaiting Author', color: '#3b82f6', bg: '#dbeafe' },
      reviewer_approved: { label: 'Reviewer Approved', color: '#059669', bg: '#d1fae5' },
      assigned: { label: 'Assigned', color: '#047857', bg: '#d1fae5' },
    };

    const config = statusConfig[status];
    if (!config) return null;

    return (
      <Box
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          px: 0.75,
          py: 0.25,
          borderRadius: '4px',
          backgroundColor: config.bg,
          color: config.color,
          fontSize: '0.65rem',
          fontWeight: 600,
          textTransform: 'uppercase',
        }}
      >
        {config.label}
      </Box>
    );
  };

  // Check if user can edit a specific version
  const canEditVersion = (version) => {
    // Authors can edit any version
    if (isAuthor) return true;
    // Designers can edit their own versions
    if (isDesigner && version.isOwner) return true;
    // Designers can edit ANY version if it's in_progress with authorFeedback (author forwarded for revision)
    if (isDesigner && version.coverStatus === 'in_progress' && version.authorFeedback) return true;
    return false;
  };

  // Check if user can delete a specific version
  const canDeleteVersion = (version) => {
    // Can't delete if only one version
    if (versions.length <= 1) return false;
    // Authors can delete any version that isn't reviewer_approved or assigned
    if (isAuthor && version.coverStatus !== 'reviewer_approved' && version.coverStatus !== 'assigned') return true;
    // Designers can only delete versions they created AND that haven't been sent for approval
    if (isDesigner && version.isOwner && !version.coverStatus) return true;
    return false;
  };

  const selectedVersion = versions.find(v => v.id === menuVersionId);

  // Designers and authors can always create versions (unless cover is fully approved by reviewer)
  const canCreateVersion = isAuthor && !disabled;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
          Your Book Covers
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {canCreateVersion && (
            <Tooltip title="Create new version">
              <span>
                <IconButton
                  onClick={onCreateVersion}
                  disabled={loading}
                  sx={{
                    backgroundColor: '#1976d2',
                    color: 'white',
                    '&:hover': { backgroundColor: '#1565c0' },
                    '&:disabled': { backgroundColor: '#ccc' },
                  }}
                  size="small"
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : versions.length === 0 ? (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: 2,
            border: '1px dashed #ccc',
          }}
        >
          <Typography color="text.secondary">
            No cover versions yet. Create your first version to get started.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          {/* Left arrow */}
          <IconButton
            onClick={() => scrollBy(-1)}
            disabled={!canScrollLeft}
            size="small"
            sx={{
              flexShrink: 0,
              mr: 0.5,
              backgroundColor: canScrollLeft ? '#413B8D' : 'transparent',
              color: canScrollLeft ? 'white' : '#ccc',
              '&:hover': { backgroundColor: canScrollLeft ? '#2e2970' : 'transparent' },
              '&:disabled': { backgroundColor: 'transparent' },
              transition: 'all 0.2s',
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          {/* Scrollable card row */}
          <Box
            ref={scrollRef}
            onScroll={updateScrollState}
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              flex: 1,
              justifyContent: versions.length <= 4 ? 'center' : 'flex-start',
              px: 0.5,
              py: 1,
            }}
          >
          {versions.map((version) => {
            const isActive = version.id === activeVersionId;
            const isPrimary = version.id === (primaryVersionId ?? activeVersionId);

            return (
              <Box
                key={version.id}
                onClick={() => { if (!isActive && !disabled) onSelectVersion?.(version.id); }}
                sx={{
                  flexShrink: 0,
                  width: 195,
                  position: 'relative',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: isActive ? '3px solid #1976d2' : '2px solid #e0e0e0',
                  cursor: isActive || disabled ? 'default' : 'pointer',
                  transition: 'all 0.2s ease',
                  backgroundColor: '#fff',
                  '&:hover': (disabled || isActive) ? {} : {
                    borderColor: '#1976d2',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {/* Thumbnail */}
                <Box
                  sx={{
                    aspectRatio: '2/3',
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  <VersionCardPreview version={version} />

                  {/* Status Badge */}
                  {getStatusBadge(version.coverStatus)}

                  {/* Active Indicator */}
                  {isActive && (
                    <Tooltip title="Active version">
                      <CheckCircleIcon
                        sx={{
                          position: 'absolute',
                          bottom: 4,
                          left: 4,
                          color: '#1976d2',
                          backgroundColor: 'white',
                          borderRadius: '50%',
                          fontSize: 20,
                        }}
                      />
                    </Tooltip>
                  )}

                  {/* Creator Role Badge */}
                  {version.createdByRole && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 4,
                        right: 4,
                        px: 0.5,
                        py: 0.25,
                        borderRadius: '4px',
                        backgroundColor: version.createdByRole === 'designer' ? '#e8f5e9' : '#fff3e0',
                        color: version.createdByRole === 'designer' ? '#2e7d32' : '#e65100',
                        fontSize: '0.55rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }}
                    >
                      {version.createdByRole === 'designer' ? '🎨' : '✍️'}
                    </Box>
                  )}

                  {/* Collaborator Count Badge */}
                  {(version.collaborators?.length > 0) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        px: 0.5,
                        py: 0.25,
                        borderRadius: '4px',
                        backgroundColor: '#ede9fe',
                        color: '#5A52A3',
                        fontSize: '0.6rem',
                        fontWeight: 600,
                      }}
                    >
                      👥 {version.collaborators.length}
                    </Box>
                  )}

                  {/* Purchased Badge */}
                  {version.isPurchased && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: version.collaborators?.length > 0 ? 24 : 4,
                        left: 4,
                        px: 0.5,
                        py: 0.25,
                        borderRadius: '4px',
                        backgroundColor: '#fef3c7',
                        color: '#92400e',
                        fontSize: '0.55rem',
                        fontWeight: 600,
                      }}
                    >
                      📦
                    </Box>
                  )}
                </Box>

                {/* Version Info */}
                <Box
                  sx={{
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                  }}
                >
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? '#1976d2' : '#333',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.8rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.25,
                      }}
                    >
                      {isPrimary && (
                        <Tooltip title="Primary version">
                          <StarIcon sx={{ fontSize: 14, color: '#D4AF37' }} />
                        </Tooltip>
                      )}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {version.name}
                      </span>
                    </Typography>
                    {(version.createdByName || version.createdByRole) && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#999',
                          fontSize: '0.65rem',
                        }}
                      >
                        by {version.createdByName || version.createdByRole}
                      </Typography>
                    )}
                    {version.coverType && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#7c3aed',
                          fontSize: '0.6rem',
                          fontWeight: 500,
                        }}
                      >
                        {version.coverType.replace(/_/g, ' ')}
                      </Typography>
                    )}
                  </Box>
                  
                  {canEditVersion(version) && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, version.id)}
                      disabled={disabled}
                      sx={{ ml: 0.5, p: 0.5 }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            );
          })}
          </Box>

          {/* Right arrow */}
          <IconButton
            onClick={() => scrollBy(1)}
            disabled={!canScrollRight}
            size="small"
            sx={{
              flexShrink: 0,
              ml: 0.5,
              backgroundColor: canScrollRight ? '#413B8D' : 'transparent',
              color: canScrollRight ? 'white' : '#ccc',
              '&:hover': { backgroundColor: canScrollRight ? '#2e2970' : 'transparent' },
              '&:disabled': { backgroundColor: 'transparent' },
              transition: 'all 0.2s',
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleRenameClick} disabled={!selectedVersion || !canEditVersion(selectedVersion)}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Rename
        </MenuItem>
        {/* Author only: primary is one shared choice for the book, so a designer
            setting it would change what the author and every other collaborator
            sees. Switching versions stays open to everyone — that is per-user.
            Disabled against the PRIMARY version, not the active one: the two are
            now separate, and an author editing a version they have not yet made
            primary must be able to make it primary. */}
        {isAuthor && (
        <MenuItem
          onClick={() => {
            if (menuVersionId && menuVersionId !== primaryVersionId) {
              onSetPrimary?.(menuVersionId);
            }
            handleMenuClose();
          }}
          disabled={!selectedVersion || selectedVersion.id === primaryVersionId}
          sx={{ color: (!selectedVersion || selectedVersion.id === primaryVersionId) ? 'text.disabled' : '#D4AF37' }}
        >
          {selectedVersion?.id === primaryVersionId
            ? <StarIcon fontSize="small" sx={{ mr: 1 }} />
            : <StarOutlineIcon fontSize="small" sx={{ mr: 1 }} />}
          Set As Primary
        </MenuItem>
        )}
        {/* Show "Send to Author" for: owned versions without status, in_progress, or with authorFeedback (forwarded revision) */}
        {isDesigner && selectedVersion?.cover_url && (
          (selectedVersion?.isOwner && (!selectedVersion?.coverStatus || selectedVersion?.coverStatus === 'in_progress')) ||
          (selectedVersion?.coverStatus === 'in_progress' && selectedVersion?.authorFeedback)
        ) && (
          <MenuItem
            onClick={() => {
              if (menuVersionId) {
                onSendToAuthor?.(menuVersionId);
              }
              handleMenuClose();
            }}
            sx={{ color: 'primary.main' }}
          >
            <SendIcon fontSize="small" sx={{ mr: 1 }} />
            Send to Author
          </MenuItem>
        )}
        <MenuItem onClick={handleDuplicateClick}>
          <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} />
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuVersionId) {
              onViewDetails?.(menuVersionId);
            }
            handleMenuClose();
          }}
        >
          <InfoOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        {isAuthor && (
          <MenuItem
            onClick={() => {
              if (menuVersionId) {
                onAddCollaborators?.(menuVersionId);
              }
              handleMenuClose();
            }}
          >
            <GroupAddIcon fontSize="small" sx={{ mr: 1 }} />
            Add Collaborators
          </MenuItem>
        )}
        <MenuItem
          onClick={handleDeleteClick}
          disabled={!selectedVersion || !canDeleteVersion(selectedVersion)}
          sx={{ color: (!selectedVersion || !canDeleteVersion(selectedVersion)) ? 'text.disabled' : 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename Version</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Version Name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleRenameConfirm()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRenameConfirm}
            variant="contained"
            disabled={!renameValue.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => { setDeleteDialogOpen(false); setDeleteVersionId(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Version</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{versions.find(v => v.id === deleteVersionId)?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialogOpen(false); setDeleteVersionId(null); }}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
